import { useState, useEffect } from 'react';
import API, { formatError } from '../api/client';
import { Plus, Apple, Trash2, X, Scale } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import PatientSelect from '../components/PatientSelect';

const EMPTY_FORM = {
  patient_id: '', date: new Date().toISOString().split('T')[0],
  poids: '', taille: '', tour_de_taille: '',
  objectif_calorique: '', apport_calorique_actuel: '',
  type_regime: '', activite_physique: '', objectif_poids: '', notes: ''
};

const IMC_LABEL = (v) => {
  if (v < 16) return ['Dénutrition sévère', 'text-red-700 bg-red-100'];
  if (v < 18.5) return ['Maigreur', 'text-blue-700 bg-blue-100'];
  if (v < 25) return ['Normal', 'text-emerald-700 bg-emerald-100'];
  if (v < 30) return ['Surpoids', 'text-amber-700 bg-amber-100'];
  if (v < 35) return ['Obésité modérée', 'text-orange-700 bg-orange-100'];
  return ['Obésité sévère', 'text-red-700 bg-red-100'];
};

export default function NutritionPage() {
  const [records, setRecords] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async (pid = selectedPatient) => {
    const params = pid ? { patient_id: pid } : {};
    const [r, p] = await Promise.all([
      API.get('/nutrition', { params }),
      API.get('/patients'),
    ]);
    setRecords(r.data);
    setPatients(p.data.data ?? p.data);
  };

  useEffect(() => { load(); }, []);

  const handlePatientChange = (pid) => {
    setSelectedPatient(pid);
    setForm(f => ({ ...f, patient_id: pid }));
    load(pid);
  };

  const imc = form.poids && form.taille ? parseFloat((parseFloat(form.poids) / (parseFloat(form.taille) / 100) ** 2).toFixed(1)) : null;

  const handleSave = async () => {
    setError('');
    if (!form.patient_id) { setError('Veuillez sélectionner un patient.'); return; }
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === '') payload[k] = null;
        else if (['poids', 'taille', 'tour_de_taille', 'objectif_poids'].includes(k)) payload[k] = parseFloat(v) || null;
        else if (['objectif_calorique', 'apport_calorique_actuel'].includes(k)) payload[k] = parseInt(v) || null;
        else payload[k] = v;
      });
      await API.post('/nutrition', payload);
      setShowForm(false);
      setForm({ ...EMPTY_FORM, patient_id: selectedPatient });
      load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet enregistrement ?')) return;
    await API.delete(`/nutrition/${id}`);
    load();
  };

  const chartData = [...records].reverse().map(r => ({
    date: r.date,
    'Poids (kg)': r.poids,
    'IMC': r.imc,
  }));

  const F = ({ label, children }) => (
    <div className="flex flex-col space-y-1">
      <label className="text-xs font-medium text-stone-600">{label}</label>
      {children}
    </div>
  );

  const inp = (field, type = 'text', placeholder = '') => (
    <input type={type} placeholder={placeholder} value={form[field] || ''}
      onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
      className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" />
  );

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="nutrition-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-900 flex items-center gap-2">
            <Apple size={22} className="text-emerald-600" />
            Nutrition & Diète
          </h1>
          <p className="text-stone-500 text-sm mt-0.5">Suivi nutritionnel et IMC</p>
        </div>
        <button data-testid="new-nutrition-record-button" onClick={() => { setShowForm(true); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors">
          <Plus size={17} /> Nouveau Bilan
        </button>
      </div>

      {/* Patient filter */}
      <div className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-4">
        <label className="text-sm font-medium text-stone-600 whitespace-nowrap">Filtrer par patient:</label>
        <PatientSelect
          testId="nutrition-patient-filter"
          className="flex-1 max-w-md"
          placeholder="Tous les patients — rechercher (nom, CIN, id)…"
          value={selectedPatient}
          patients={patients}
          allowClear
          onChange={(id) => handlePatientChange(id)}
        />
      </div>

      {/* Chart */}
      {chartData.length > 1 && (
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <h3 className="font-heading font-semibold text-stone-700 mb-4">Évolution du poids et de l'IMC</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#78716c' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#78716c' }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#78716c' }} domain={[10, 40]} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: '8px', border: '1px solid #e7e5e4' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine yAxisId="right" y={18.5} stroke="#93c5fd" strokeDasharray="4 4" />
              <ReferenceLine yAxisId="right" y={25} stroke="#86efac" strokeDasharray="4 4" />
              <ReferenceLine yAxisId="right" y={30} stroke="#fcd34d" strokeDasharray="4 4" />
              <Line yAxisId="left" type="monotone" dataKey="Poids (kg)" stroke="#059669" strokeWidth={2} dot={{ r: 4 }} />
              <Line yAxisId="right" type="monotone" dataKey="IMC" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
          <div className="flex gap-4 mt-2 text-xs text-stone-500">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-300 inline-block"></span> IMC 18.5 (maigreur)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-400 inline-block"></span> IMC 25 (surpoids)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-400 inline-block"></span> IMC 30 (obésité)</span>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full" data-testid="nutrition-table">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              {['Date', 'Patient', 'Poids', 'Taille', 'IMC', 'Régime', 'Activité', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {records.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-stone-400 text-sm">
                <Scale size={36} className="text-stone-200 mx-auto mb-2" />
                Aucun bilan nutritionnel
              </td></tr>
            )}
            {records.map(r => {
              const patient = patients.find(p => p.id === r.patient_id);
              const [imcLabel, imcClass] = r.imc ? IMC_LABEL(r.imc) : ['—', 'text-stone-500'];
              return (
                <tr key={r.id} className="hover:bg-stone-50/60 transition-colors" data-testid="nutrition-record-row">
                  <td className="px-4 py-3 text-sm text-stone-700">{r.date}</td>
                  <td className="px-4 py-3 text-sm font-medium text-stone-800">{patient ? `${patient.prenom} ${patient.nom}` : '—'}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{r.poids ? `${r.poids} kg` : '—'}</td>
                  <td className="px-4 py-3 text-sm text-stone-600">{r.taille ? `${r.taille} cm` : '—'}</td>
                  <td className="px-4 py-3">
                    {r.imc ? (
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${imcClass}`}>
                        {r.imc} — {imcLabel}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-600 max-w-[120px] truncate">{r.type_regime || '—'}</td>
                  <td className="px-4 py-3 text-sm text-stone-600 max-w-[120px] truncate">{r.activite_physique || '—'}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleDelete(r.id)}
                      className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="nutrition-form-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white z-10">
              <h2 className="font-heading font-semibold text-stone-800 text-lg">Nouveau Bilan Nutritionnel</h2>
              <button onClick={() => setShowForm(false)} className="p-1 text-stone-400 hover:text-stone-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">{error}</div>}

              <div className="grid grid-cols-2 gap-4">
                <F label="Patient *">
                  <PatientSelect
                    value={form.patient_id}
                    patients={patients}
                    onChange={(id) => setForm(f => ({ ...f, patient_id: id }))}
                  />
                </F>
                <F label="Date">{inp('date', 'date')}</F>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Mesures Anthropométriques</h3>
                <div className="grid grid-cols-3 gap-4">
                  <F label="Poids (kg)">{inp('poids', 'number', '70')}</F>
                  <F label="Taille (cm)">{inp('taille', 'number', '170')}</F>
                  <F label="Tour de taille (cm)">{inp('tour_de_taille', 'number', '80')}</F>
                </div>
                {imc && (
                  <div className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${IMC_LABEL(imc)[1]}`}>
                    <Scale size={16} />
                    IMC: {imc} — {IMC_LABEL(imc)[0]}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Alimentation & Objectifs</h3>
                <div className="grid grid-cols-2 gap-4">
                  <F label="Apport calorique actuel (kcal/jour)">{inp('apport_calorique_actuel', 'number', '2000')}</F>
                  <F label="Objectif calorique (kcal/jour)">{inp('objectif_calorique', 'number', '1800')}</F>
                  <F label="Objectif de poids (kg)">{inp('objectif_poids', 'number', '65')}</F>
                  <F label="Activité physique">{inp('activite_physique', 'text', 'Sédentaire / Légère / Modérée / Intense')}</F>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">Régime Alimentaire</h3>
                <F label="Type de régime">
                  <select value={form.type_regime} onChange={e => setForm(f => ({ ...f, type_regime: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500">
                    <option value="">Sélectionner...</option>
                    <option value="Équilibré">Équilibré</option>
                    <option value="Hypocalorique">Hypocalorique</option>
                    <option value="Hypoglucidique">Hypoglucidique</option>
                    <option value="Hyperprotéiné">Hyperprotéiné</option>
                    <option value="Sans gluten">Sans gluten</option>
                    <option value="Sans lactose">Sans lactose</option>
                    <option value="Méditerranéen">Méditerranéen</option>
                    <option value="Diabétique">Diabétique</option>
                    <option value="Hyposodé">Hyposodé</option>
                    <option value="Végétarien">Végétarien</option>
                  </select>
                </F>
              </div>

              <F label="Notes">
                <textarea rows={3} placeholder="Habitudes alimentaires, intolérances, recommandations..." value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none" />
              </F>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-stone-200 bg-stone-50">
              <button onClick={() => setShowForm(false)} className="px-5 py-2 border border-stone-300 bg-white text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-100">Annuler</button>
              <button data-testid="save-nutrition-button" onClick={handleSave} disabled={saving}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
